/**
 * Post-purchase result modal.
 *
 * Purely informative — shown after the payment has been finalized. It states whether the
 * payment succeeded or failed and offers a single "Close" button (no other action). The
 * caller owns navigation: `onClose` decides where the user lands.
 *
 * Props:
 *  - isOpen                 — render gate
 *  - status                 — "success" | "failed"
 *  - message                — body copy (purpose-specific success text or failure reason)
 *  - onClose()              — invoked by the Close button
 */
import { useTranslation } from "react-i18next";
import { FiCheck, FiX } from "react-icons/fi";

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 16,
};
const card = {
  background: "#fff",
  padding: "32px 28px 28px",
  borderRadius: 8,
  width: "100%",
  maxWidth: 420,
  boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  textAlign: "center",
  fontFamily: "system-ui, sans-serif",
};
const iconWrap = (ok) => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  margin: "0 auto 18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: ok ? "#ecfdf5" : "#fef2f2",
  color: ok ? "#059669" : "#dc2626",
});
const titleStyle = {
  margin: "0 0 8px",
  fontSize: 20,
  fontWeight: 700,
  color: "#111827",
};
const msgStyle = {
  margin: 0,
  fontSize: 15,
  lineHeight: 1.55,
  color: "#6b7280",
};
const closeBtn = {
  marginTop: 24,
  width: "100%",
  padding: "12px 16px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 5,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 15,
};

const PaymentResultModal = ({ isOpen, status, message, onClose }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const ok = status === "success";

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-labelledby="payment-result-title">
      <div style={card}>
        <div style={iconWrap(ok)} aria-hidden="true">
          {ok ? <FiCheck size={34} strokeWidth={3} /> : <FiX size={34} strokeWidth={3} />}
        </div>

        <h3 id="payment-result-title" style={titleStyle}>
          {ok
            ? t("pages.subscription.paymentResult.successTitle")
            : t("pages.subscription.paymentResult.failedTitle")}
        </h3>

        {message && <p style={msgStyle}>{message}</p>}

        <button type="button" style={closeBtn} onClick={onClose} autoFocus>
          {t("pages.subscription.paymentResult.close")}
        </button>
      </div>
    </div>
  );
};

export default PaymentResultModal;
